import some from 'lodash/some';
import * as Sentry from '@sentry/vue';
import { isProdEnvironment } from '@/settings/environment.utils.js';

let sentryInitialized = false;

export function isEventFromLocalhost (event = {}) {
    const url = event?.request?.url;

    return typeof url === 'string' && (url.includes('local.pendo.io') || url.includes('localhost'));
}

// Sentry SDK v9+ renamed the global handler mechanism type from 'onerror' to
// 'auto.browser.global_handlers.onerror', so both values must be matched.
const ONERROR_MECHANISM_TYPES = ['onerror', 'auto.browser.global_handlers.onerror'];

export function isEventFromVue (event = {}) {
    return some(
        event?.exception?.values,
        ({ mechanism, type }) => type === 'CustomEvent' && ONERROR_MECHANISM_TYPES.includes(mechanism?.type),
    );
}

export function forceSentryCollection () {
    if(!window.location.search) return false;

    try {
        const searchParams = new URLSearchParams(window.location.search);

        return !!searchParams.has('forceSentryCollection', true);
    } catch {
        return false;
    }
}

export function beforeSendSentryEvent (event, hint) {
    setTimeout(() => console.error(hint?.originalException || hint?.syntheticException || 'Unknown error'), 0);

    if (isEventFromLocalhost(event)) return null;

    // Sentry listens for 'error' events on the window but its handler expects an Error object,
    // not the CustomEvent we wrap Vue component errors in. Sentry reporting is handled when
    // creating the CustomEvent so we need to drop it before sending to prevent double reporting.
    //
    // @see vue-app#applySharedConfigToVueApp
    if (isEventFromVue(event)) return null;

    return event;
}

export function setSampleRate () {
    if (!isProdEnvironment()) return 0;

    return 1;
}

export function setTraceSampleRate () {
    if (!isProdEnvironment()) return 0;

    if (forceSentryCollection()) {
        return 1;
    }

    return 0.40;
}

export function initSentry (router) {
    if (!isProdEnvironment() || sentryInitialized) return;

    const releaseVersion = __PROCESS__.PENDO_RELEASE;

    try {
        Sentry.init({
            release: `engage-ui@${releaseVersion}`,
            // eslint-disable-next-line internal-rules/no-email-addresses
            dsn: 'https://3599373947c84bd49c23de5b7f838908@o69161.ingest.sentry.io/4504399166046208',
            integrations: [
                Sentry.browserTracingIntegration({
                    router,
                    instrumentPageLoad: true,
                    instrumentNavigation: true,
                    // wait 5s after route navigation before closing sentry's current reporting span. Ensures that
                    // we catch aggs that don't get kicked off for a second or two until after route load
                    idleTimeout: 5_000,
                    // max timeout for any individual metric. Especially with long-running aggs, this will give us
                    // better insight into exactly how long they take
                    childSpanTimeout: 90_000,
                    // needs to be longer than childSpanTimeout, to make sure we don't truncate it prematurely
                    finalTimeout: 120_000,
                }),
            ],
            attachStacktrace: true,
            environment: __PROCESS__.PENDO_ENV,
            sampleRate: setSampleRate(),
            tracesSampleRate: setTraceSampleRate(),
            beforeSend: beforeSendSentryEvent,
        });
        sentryInitialized = true;
    } catch (error) {

        console.error(error);
    }
}

export function attachSentryVueApp (app) {
    if (!sentryInitialized || !app) return;

    Sentry.addIntegration(
        Sentry.vueIntegration({
            app,
            attachErrorHandler: false,
        }),
    );
}

/** @internal test helper */
export function resetSentryForTests () {
    sentryInitialized = false;
}
